import React, { useContext } from 'react';

import { makeVar } from '@apollo/client';
import { useReactiveVar } from '@apollo/client/react';
import { SelectRelatedDocumentDialog, UploadFilesPicker } from 'src/components';
import { Apollo } from 'src/gql/generated';

import { DocumentGridReportIdContext } from './RepositoryContextProviders';

interface OrphanResolutionState {
  dialogType: 'select' | 'upload';
  excludeDocumentId: UUID | undefined;
  onResolve: (newDocumentId: UUID) => Promise<void>;
  sourceDocumentName: string;
}

const orphanResolutionVar = makeVar<OrphanResolutionState | null>(null);

export function openOrphanResolution(state: OrphanResolutionState): void {
  orphanResolutionVar(state);
}

export function closeOrphanResolution(): void {
  orphanResolutionVar(null);
}

export function OrphanResolutionDialogs(): JSX.Element | null {
  const state = useReactiveVar(orphanResolutionVar);
  const repositoryId = useContext(DocumentGridReportIdContext);

  const handleClose = (): void => {
    closeOrphanResolution();
  };

  if (!state || !repositoryId) return null;

  switch (state.dialogType) {
    case 'select':
      return (
        <SelectRelatedDocumentDialog
          open
          excludeDocumentId={state.excludeDocumentId}
          repositoryId={repositoryId}
          sourceDocumentName={state.sourceDocumentName}
          onClose={handleClose}
          onSelect={async selectedDocId => {
            await state.onResolve(selectedDocId);
            closeOrphanResolution();
          }}
        />
      );
    case 'upload':
      return (
        <UploadFilesPicker
          open
          domContainer="orphan-resolution-upload"
          pickerOpts={{
            afterUploadDone: async res => {
              closeOrphanResolution();
              const newDocId = res.data?.createFiles?.at(0)?.id ?? null;
              if (newDocId) await state.onResolve(newDocId);
            },
            maxFiles: 1,
            onClose: handleClose,
            props: {
              documentVariant: Apollo.DocumentVariant.Document,
              isSigned: false,
            },
            reportId: repositoryId,
          }}
        />
      );
  }
}
